import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { useDRBFMCaseWorkflow } from '../../../hooks/useDRBFMCaseWorkflow';
import { drbfmService } from '../../../services/drbfm.service';
import { resolveApiErrorMessage } from '../../../utils/apiErrorMessages';
import { LoadingButton } from '../../common/LoadingButton';
import DRBFMResultsStep from '../steps/DRBFMResultsStep';
import DRBFMCaseIntakeStep from './DRBFMCaseIntakeStep';
import DRBFMChangeReviewStep from './DRBFMChangeReviewStep';
import DRBFMPrecheckStep from './DRBFMPrecheckStep';

interface DRBFMCaseWorkflowProps {
  cadEnabled?: boolean | null;
  /** Lets the page ask before leaving case mode throws this case away. */
  onProgressChange?: (inProgress: boolean) => void;
}

const DRBFMCaseWorkflow: React.FC<DRBFMCaseWorkflowProps> = ({
  cadEnabled = null,
  onProgressChange,
}) => {
  const { t } = useTranslation();
  const workflow = useDRBFMCaseWorkflow();
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const inProgress = workflow.currentCaseId !== null || workflow.caseStep !== 'case-intake';

  useEffect(() => {
    onProgressChange?.(inProgress);
  }, [inProgress, onProgressChange]);

  const handleExport = useCallback(async () => {
    const session = workflow.results?.session;
    if (!session) {
      setExportError(t('drbfm.case.exportMissing', 'エクスポート対象のDRBFM結果がありません'));
      return;
    }

    setIsExporting(true);
    setExportError(null);
    try {
      const filename = `drbfm_case_${session.itemName}_${session.id.slice(0, 8)}.html`;
      await drbfmService.downloadExportedHTML(session.id, filename);
    } catch (err) {
      setExportError(resolveApiErrorMessage(t, err, { fallbackKey: 'drbfm.case.exportFailed' }));
    } finally {
      setIsExporting(false);
    }
  }, [workflow.results?.session, t]);

  const renderStep = () => {
    switch (workflow.caseStep) {
      case 'case-intake':
        return (
          <DRBFMCaseIntakeStep
            isLoading={workflow.isLoading}
            onSubmit={workflow.createAndExtract}
          />
        );

      case 'change-review':
        return (
          <DRBFMChangeReviewStep
            changes={workflow.designChanges}
            isLoading={workflow.isLoading}
            onChange={workflow.setDesignChanges}
            onSubmit={workflow.saveChangesAndGeneratePrecheck}
            onBack={workflow.reset}
          />
        );

      case 'precheck':
        return (
          <DRBFMPrecheckStep
            items={workflow.precheckItems}
            isLoading={workflow.isLoading}
            onChange={workflow.setPrecheckItems}
            onSubmit={workflow.saveAnswersAndAnalyze}
            onBack={() => workflow.setCaseStep('change-review')}
          />
        );

      case 'analysis':
        return (
          <div className="flex min-h-[280px] items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <Spinner size="lg" label={t('drbfm.case.analyzingLabel', 'DRBFMケース分析中')} />
              <p className="revy-cjk text-base leading-[1.6] text-muted-foreground">
                {t('drbfm.case.analyzingLabel', 'DRBFMケース分析中')}
              </p>
            </div>
          </div>
        );

      case 'results':
        return (
          <div className="flex flex-col gap-4">
            {!workflow.results && workflow.latestAnalysisSessionId && (
              <div className="flex justify-end">
                <LoadingButton
                  variant="outline"
                  loading={workflow.isLoading}
                  loadingText={t('drbfm.case.refetching', '再取得中')}
                  onClick={workflow.retryFetchResults}
                >
                  <RefreshCw strokeWidth={1.9} />
                  <span className="revy-cjk">
                    {t('drbfm.case.refetchResults', '結果を再取得')}
                  </span>
                </LoadingButton>
              </div>
            )}
            <DRBFMResultsStep
              results={workflow.results}
              onExport={handleExport}
              onNewAnalysis={workflow.reset}
              isExporting={isExporting}
              exportError={exportError}
              drbfmSessionId={workflow.results?.session?.id}
              drbfmItemName={workflow.results?.session?.itemName}
              cadEnabled={cadEnabled}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {workflow.error && (
        <Alert variant="destructive">
          <AlertCircle strokeWidth={1.9} />
          <AlertDescription className="revy-cjk text-sm leading-[1.6]">
            {workflow.error}
          </AlertDescription>
        </Alert>
      )}
      {renderStep()}
    </div>
  );
};

export default DRBFMCaseWorkflow;
